import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { FormError } from "@/components/ui/form-error";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useMutation } from "@apollo/client";
import { ADD_PROJECT } from "../../../../GraphQL/settings/mutations";
import { EToastColorStatus, EToastPosition } from "../../../../interfaces";

const addProjectSchema = Yup.object().shape({
  title: Yup.string().required("Title is required"),
  description: Yup.string(),
});

interface IAddModalProps {
  showAddModal: boolean;
  onCloseAddModal: () => void;
  reloadProjects: () => void;
  onHandleCreateToast: (
    color    : EToastColorStatus,
    position : EToastPosition,
    message  : string,
  ) => void;
}

const AddProjectModal = ({
  showAddModal,
  onCloseAddModal,
  reloadProjects,
  onHandleCreateToast = () => {},
}: IAddModalProps) => {
  const [allowInvitees, setAllowInvitees] = useState(false);

  const [addProject] = useMutation(ADD_PROJECT, {
    onCompleted() {
      onCloseAddModal();
      onHandleCreateToast(
        EToastColorStatus.SUCCESS,
        EToastPosition.BOTTOM_END,
        "Added new project successfully",
      );
      reloadProjects();
    },
    onError() {
      onHandleCreateToast(
        EToastColorStatus.FAILED,
        EToastPosition.BOTTOM_END,
        "Error, something went wrong",
      );
    },
  });

  const formik = useFormik({
    initialValues: {
      title: '',
      description: '',
    },
    validationSchema: addProjectSchema,
    onSubmit: (values) => {
      const { title, description } = values;
      addProject({ variables: { title, description }});
    },
  });

  const { errors, touched } = formik;

  return (
    <Dialog open={showAddModal} onOpenChange={onCloseAddModal}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Add New Project</DialogTitle>
        </DialogHeader>
        <form onSubmit={formik.handleSubmit} className="space-y-lg">
          <div className="space-y-sm">
            <Label htmlFor="title" className="text-base font-medium">
              Title
            </Label>
            <Input
              id="title"
              name="title"
              type="text"
              placeholder="Enter project title"
              onChange={formik.handleChange}
              value={formik?.values?.title}
            />
            <FormError>
              {errors?.title && touched?.title ? errors?.title : null}
            </FormError>
          </div>

          <div className="space-y-sm">
            <Label htmlFor="description" className="text-base font-medium">
              Description
            </Label>
            <Textarea
              id="description"
              name="description"
              rows={3}
              placeholder="Enter project description"
              onChange={formik.handleChange}
              value={formik?.values?.description}
            />
          </div>

          <div className="flex items-center justify-between p-md bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
            <div className="flex-1">
              <Label htmlFor="allow-invitees" className="text-base font-medium cursor-pointer">
                Allow invitees to add roles &amp; candidates
              </Label>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                Grant project members permission to add content
              </p>
            </div>
            <Switch
              id="allow-invitees"
              checked={allowInvitees}
              onCheckedChange={setAllowInvitees}
            />
          </div>
        </form>
        <DialogFooter>
          <Button variant="outline" onClick={onCloseAddModal}>
            Cancel
          </Button>
          <Button variant="default" onClick={formik.submitForm}>
            Create Project
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default AddProjectModal;
